import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaFacebook,
  FaWhatsapp,
  FaLinkedin,
  FaPinterest,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { SiWaze } from "react-icons/si";
import colors from "../styles/colors";
import config from "../utils/config";
import { useTranslation } from "react-i18next";
import SEO from "../utils/SEO";
import { SlArrowDown } from "react-icons/sl";

const Contact: React.FC = () => {
  const { t } = useTranslation();
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.style.overflowX = "hidden";
    return () => {
      document.body.style.overflowX = "auto";
    };
  }, []);

  const scrollDown = () => {
    // Az oldal lefelé görgetése 600px-t
    window.scrollBy({ top: 700, left: 0, behavior: "smooth" });
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center font-serif overflow-x-hidden"
      style={{ backgroundColor: colors.pastelCream }}
    >
      {/* SEO meta tags */}
      <SEO
        title={t("seo_meta.contact.metaTitle")}
        description={t("seo_meta.contact.metaDescription")}
      />
      {/* Title Section */}
      <motion.div
        className="h-[80vh] lg:h-screen w-full flex flex-col items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: "url('/assets/images/contact/wood_bg1.webp')",
        }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Sötétítő réteg  */}
        <div className="h-[80vh] lg:h-screen absolute inset-0 bg-black opacity-10"></div>
        {/* title */}
        <motion.h1
          className="lg:text-8xl text-5xl md:text-7xl mt-24 md:mt-48 z-10"
          style={{ color: colors.lightCream }}
          initial={{ y: 35, opacity: 0 }}
          whileInView={{
            y: 0,
            opacity: 1,
            transition: { delay: 0.2, duration: 0.6 },
          }}
          viewport={{ once: false, amount: 0.5 }}
        >
          {t("contactPage.title")}
        </motion.h1>
        {/* subtitle */}
        <motion.p
          className="lg:text-3xl text-lg md:text-2xl mt-4 sm:mt-8 z-10"
          style={{ color: colors.lightCream }}
          initial={{ y: 20, opacity: 0 }}
          whileInView={{
            y: 0,
            opacity: 1,
            transition: { delay: 0.5, duration: 0.6 },
          }}
          viewport={{ once: false, amount: 0.5 }}
        >
          {t("contactPage.subTitle")}
        </motion.p>
        {/* icon */}
        <motion.div
          className="mt-24 sm:mt-48"
          initial={{ y: 20, opacity: 0 }}
          whileInView={{
            y: 0,
            opacity: 1,
            transition: { delay: 1.5, duration: 1 },
          }}
          viewport={{ once: false, amount: 0.5 }}
        >
          <SlArrowDown
            className="text-3xl sm:text-4xl text-white animate-bounce hover:text-gray-300 cursor-pointer"
            onClick={scrollDown}
          />
        </motion.div>
      </motion.div>

      {/* Title and Contact Section Wrapper */}
      <div
        className="w-full flex flex-col justify-start items-center"
        style={{ backgroundColor: colors.pastelCream }}
      >
        {/* Title Section */}
        <motion.div
          className="w-full h-auto flex flex-col md:px-12 lg:px-20 p-2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.8 }}
            className="lg:text-8xl text-4xl md:text-7xl mt-12 font-serif text-transparent bg-clip-text p-4 text-center"
            style={{
              backgroundImage: `linear-gradient(to right, ${colors.darkBrown}, ${colors.pantoneBrown})`,
            }}
          >
            {t("contactPage.descTitle")}
          </motion.h1>
          {/* További szöveg */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:text-2xl text-lg md:text-xl font-serif p-4 text-center"
            style={{ color: colors.darkBrown }}
          >
            {t("contactPage.descSubtitle")}
          </motion.p>
        </motion.div>

        {/* Contact Section */}
        <motion.div
          className="w-full min-h-[100vh] flex flex-col md:flex-row items-center justify-center px-6 sm:px-12 lg:px-2 space-y-12 md:space-y-0 md:space-x-12 pt-6 flex-wrap"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          style={{ backgroundColor: colors.pastelCream }}
        >
          {/* Map (Left Side - Square) */}
          <div className="w-full md:w-[80%] lg:w-1/2 max-w-[500px] aspect-square rounded-xl overflow-hidden shadow-lg relative z-10">
            <iframe
              title="Company Location"
              src={config.mapEmbedUrl}
              width="100%"
              height="100%"
              className="rounded-md"
              style={{ border: "0", minHeight: "300px", aspectRatio: "1 / 1" }}
              allowFullScreen
            ></iframe>
          </div>

          {/* Contact Info (Right Side) */}
          <div className="w-full md:max-w-full lg:w-1/2 space-y-6 text-center md:text-left relative z-20 p-6">
            <h2
              className="lg:text-6xl text-4xl md:text-6xl font-serif text-transparent bg-clip-text p-4 lg:pl-16"
              style={{
                backgroundImage: `linear-gradient(to right, ${colors.darkBrown}, ${colors.pantoneBrown})`,
              }}
            >
              {t("contactPage.contactInfo")}
            </h2>

            {/* Cím és ikon egymás mellett */}
            <p
              className="lg:text-2xl text-lg md:text-xl font-serif p-4 md:lg:pl-8 lg:pl-16"
              style={{ color: colors.darkBrown }}
            >
              {t("contactPage.contactSub")}
            </p>
            <div className="flex items-center justify-center md:justify-start space-x-4 p-2 lg:pl-16">
              {/* ADDRESS */}
              <a
                href={config.googleMapsAddressLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-row items-center space-x-4 p-2 sm:p-4 rounded-lg transition"
                style={{ color: colors.darkBrown }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = colors.primaryGreen;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = colors.darkBrown;
                }}
              >
                <FaMapMarkerAlt className="lg:text-4xl text-3xl md:text-4xl" />
                <span className="lg:text-2xl text-lg md:text-xl font-serif">
                  {config.address}
                </span>
              </a>
            </div>

            {/* Contact Grid */}
            <div className="grid grid-cols-2 gap-4 mt-4">
              {/* EMAIL */}
              <a
                href={`mailto:${config.email}`}
                className="flex flex-col items-center space-y-2 p-2 sm:p-4 rounded-lg transition"
                style={{ color: colors.darkBrown }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = colors.primaryGreen;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = colors.darkBrown;
                }}
              >
                <FaEnvelope className="lg:text-4xl text-3xl md:text-4xl" />
                <span className="lg:text-xl text-lg md:text-xl font-serif">
                  {config.email}
                </span>
              </a>
              {/* PHONE */}
              <a
                href={`tel:${config.phone}`}
                className="flex flex-col items-center space-y-2 p-2 sm:p-4 rounded-lg transition"
                style={{ color: colors.darkBrown }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = colors.primaryGreen;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = colors.darkBrown;
                }}
              >
                <FaPhone className="lg:text-4xl text-3xl md:text-4xl" />
                <span className="lg:text-xl text-lg md:text-xl font-serif">
                  {config.phone}
                </span>
              </a>
              {/* WHATSAPP */}
              <a
                href={`https://wa.me/${config.phone}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center space-y-2 p-2 sm:p-4 rounded-lg transition"
                style={{ color: colors.darkBrown }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = colors.primaryGreen;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = colors.darkBrown;
                }}
              >
                <FaWhatsapp className="lg:text-4xl text-3xl md:text-4xl" />
                <span className="lg:text-xl text-base md:text-xl font-serif">
                  {t("contactPage.whatsappDesc")}
                </span>
              </a>
              {/* WAZE */}
              <a
                href={config.wazeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center space-y-2 p-2 sm:p-4 rounded-lg transition"
                style={{ color: colors.darkBrown }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = colors.primaryGreen;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = colors.darkBrown;
                }}
              >
                <SiWaze className="lg:text-4xl text-3xl md:text-4xl" />
                <span className="lg:text-xl text-base md:text-xl font-serif">
                  {t("contactPage.wazeDesc")}
                </span>
              </a>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Horizontal Divider */}
      <div
        className="w-full max-h-[20vh] pt-24 pb-24 flex justify-center"
        style={{ backgroundColor: colors.pastelCream }}
      >
        <hr
          className="w-[23%] sm:w-[15%] md:w-[15%] lg:w-[10%] h-0.5 border-t-0 opacity-100 dark:opacity-80"
          style={{ backgroundColor: colors.darkBrown }}
        />
      </div>

      {/* Social Network Section */}
      <motion.div
        className="w-full py-16 text-center"
        style={{ backgroundColor: colors.darkBrown }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.6 }}
      >
        <h2
          className="lg:text-5xl text-3xl md:text-4xl font-serif mb-2"
          style={{ color: colors.lightCream }}
        >
          {t("contactPage.followSecTitle")}
        </h2>
        <p
          className="lg:text-3xl text-2xl md:text-3xl font-serif text-gray-400 mb-6"
          style={{ color: colors.primaryGreen, fontStyle: "italic"}}
        >
          {t("contactPage.followSub")}
        </p>

        <div className="flex justify-center space-x-8 cursor-pointer">
          <a
            href={config.facebookPage}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaFacebook
              size={40}
              style={{ color: colors.lightCream }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = colors.primaryGreen)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = colors.lightCream)
              }
            />
          </a>
          <a
            //href={config.linkedInPage}
            // target="_blank"
            // rel="noopener noreferrer"
            href={"#"}
            target={undefined}
            rel={undefined}
          >
            <FaLinkedin
              size={40}
              style={{ color: colors.lightCream }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = colors.primaryGreen)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = colors.lightCream)
              }
              onClick={(e) => {
                e.preventDefault(); // Megakadályozza a link követését
                alert("Not available at the moment.");
              }}
            />
          </a>
          <a
            //href={config.pinterestPage}
            //target="_blank"
            //rel="noopener noreferrer"
            href={"#"}
            target={undefined}
            rel={undefined}
          >
            <FaPinterest
              size={40}
              style={{ color: colors.lightCream }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = colors.primaryGreen)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = colors.lightCream)
              }
              onClick={(e) => {
                e.preventDefault(); // Megakadályozza a link követését
                alert("Not available at the moment.");
              }}
            />
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default Contact;
